import type { Module } from "vuex";
import jwt_decode from "jwt-decode";

import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import router from "@/router";

export interface User {
  refVendeur: string | null;
  nomComplet: string | null;
  societe: string | null;
  saisieFeuilleCaisse: boolean | null;
  peutCreerProposition: boolean | null;
  roles: string[] | null;
}

export interface AuthState {
  errors: Record<string, unknown>;
  user: User | Record<string, unknown>;
  isAuthenticated: boolean;
  redirectUrl: string | null;
  bitrix24AccessToken: string | null;
  bitrix24AccessTokenExpires: number;
  bitrix24RefreshToken: string | null;
}

const emptyUser: User = {
  refVendeur: null,
  nomComplet: null,
  societe: null,
  saisieFeuilleCaisse: null,
  peutCreerProposition: null,
  roles: null,
};

const AuthModule: Module<AuthState, unknown> = {
  state: () => {
    const token = JwtService.getToken();
    return {
      errors: {},
      user: token ? (jwt_decode(token) as User) : { ...emptyUser },
      isAuthenticated: !!token,
      redirectUrl: localStorage.getItem("auth.redirect_url"),
      bitrix24AccessToken: localStorage.getItem("auth.bitrix24AccessToken"),
      bitrix24AccessTokenExpires: Number(
        localStorage.getItem("auth.bitrix24AccessTokenExpires")
      ),
      bitrix24RefreshToken: localStorage.getItem("auth.bitrix24RefreshToken"),
    };
  },

  getters: {
    getCurrentUser: (state) => state.user,
    getRedirectUrl: (state) => state.redirectUrl,
    isUserAuthenticated: (state) => state.isAuthenticated,
    getBitrix24AccessToken: (state) => state.bitrix24AccessToken,
    getBitrix24AccessTokenExpires: (state) => state.bitrix24AccessTokenExpires,
    getBitrix24RefreshToken: (state) => state.bitrix24RefreshToken,
    getErrors: (state) => state.errors,
  },

  mutations: {
    [Mutations.SET_USER](state, user: User) {
      state.user = user;
    },

    [Mutations.SET_ERROR](state, error: Record<string, unknown>) {
      state.errors = { ...error };
    },

    [Mutations.SET_AUTH](
      state,
      payload: { access_token: string; expires_in: number; user: User }
    ) {
      state.isAuthenticated = true;
      state.user = payload.user;
      state.errors = {};
      JwtService.saveToken(payload.access_token);
      JwtService.saveExpiresAt(String(Date.now() + payload.expires_in * 1000));
    },

    setRedirectUrl(state, url: string) {
      state.redirectUrl = url;
      localStorage.setItem("auth.redirect_url", url);
    },

    setBitrix24AccessToken(state, accessToken: string) {
      state.bitrix24AccessToken = accessToken;
      localStorage.setItem("auth.bitrix24AccessToken", accessToken);
    },

    setBitrix24AccessTokenExpires(state, expires: number) {
      state.bitrix24AccessTokenExpires = expires;
      localStorage.setItem("auth.bitrix24AccessTokenExpires", String(expires));
    },

    setBitrix24RefreshToken(state, refreshToken: string) {
      state.bitrix24RefreshToken = refreshToken;
      localStorage.setItem("auth.bitrix24RefreshToken", refreshToken);
    },

    [Mutations.PURGE_AUTH](state) {
      state.isAuthenticated = false;
      state.user = { ...emptyUser };
      state.errors = {};
      JwtService.destroyToken();
    },
  },

  actions: {
    [Actions.VERIFY_AUTH]() {
      const tokenExpiresAt = JwtService.getExpiresAt() ?? 0;
      if (tokenExpiresAt <= Date.now()) {
        router.push({ name: "sign-in" });
      }
    },
  },
};

export default AuthModule;
